import _ from "lodash";
import moment from "moment-timezone";

import { ADHERENCE_INTERVENTIONS_QUESTIONS } from "@/features/note-content/constants/adherence-interventions";
import { copyFields } from "@/utils/objectUtils";
import { eventTypeDisplayNameFactory } from "@/components/Provider/ScheduleAvailability/ActiveScheduleDetails";
import { joinDiagnosis } from "@/utils/noteUtils";
import { LAB_FINDING_QUESTIONS } from "@/constants/lab";
import { MENTAL_STATUS_QUESTIONS } from "@/features/note-content/components/BehavioralMentalStatus";
import { ProviderEnum } from "@/core/constants";
import { SIDE_EFFECT_QUESTIONS } from "@/features/note-content/constants/side-effects";
import { SUBSTANCE_ABUSE_QUESTIONS } from "@/features/notes/(v2)/(guided-session)/constants";

const transformPatientQuestionsToSection = (
  questionSet,
  relevantSections,
  addNone = false,
  answerMapper = null,
) => {
  let reducedEntries = {};

  relevantSections.forEach((sectionId) => {
    let section = questionSet[sectionId];

    if (section == null) {
      return;
    }

    let questions = ["rating", "gad", "phq"].includes(section.type)
      ? section.questions
      : section.options;

    Object.entries(questions).forEach(([key, value]) => {
      reducedEntries[key] = value;

      if (answerMapper) {
        reducedEntries[key].answer = answerMapper(value.answer);
      }

      reducedEntries[key].label = reducedEntries[key].label
        ? reducedEntries[key].label
        : section.header;
      reducedEntries[key].type = section.type;
      if (section.screening_type) {
        reducedEntries[key].screening_type = section.screening_type;
      }
      if (section.rating_labels) {
        reducedEntries[key].rating_labels = section.rating_labels;
      }
    });
  });

  if (addNone) {
    reducedEntries = {
      none: {
        answer: false,
        label: "None",
      },
      ...reducedEntries,
    };
  }

  return reducedEntries;
};

export const carePlanToObjectives = (carePlan) => {
  let objectives = carePlan.objectives ? carePlan.objectives : [];

  if (!Array.isArray(objectives)) {
    objectives = ["objective_one", "objective_two", "objective_three"].map((objectiveKey) =>
      objectives[objectiveKey] ? objectives[objectiveKey] : "",
    );
  }

  return objectives.map((objective) => {
    return {
      actions: "",
      answer: null,
      label: objective,
      plan: "",
    };
  });
};

const transformDiagnosisToProgress = (patientDiagnosis) => {
  const allDiagnosis = joinDiagnosis(patientDiagnosis);

  return allDiagnosis.map((diagnosis) => {
    return {
      answer: null,
      assessment: "",
      label: diagnosis.label,
      plan: "",
    };
  });
};

export const createEmptyNote = (callDetails, version, patientDiagnosis) => {
  let timestamp = moment.utc(callDetails.timestamp);
  let sessionDate = timestamp.local().format("MM/DD/YYYY");
  let sessionFrom = timestamp.local();
  let sessionTo = moment
    .utc(callDetails.timestamp)
    .add(moment.duration(callDetails.allotted_time, "minutes"))
    .local();

  const providerType = callDetails.provider_role;

  let title;

  if (providerType.startsWith(ProviderEnum.BEHAVIORAL)) {
    title = `Therapy Session ${sessionDate} - (${sessionFrom.format(
      "hh:mm A",
    )}-${sessionTo.format("hh:mm A")})`;
  } else {
    title = `Prescriber Guided Session ${sessionDate} - (${sessionFrom.format(
      "hh:mm A",
    )}-${sessionTo.format("hh:mm A")})`;
  }

  return {
    call_id: callDetails.callId,
    call_name: eventTypeDisplayNameFactory(callDetails.event_type),
    content: {},
    ICD_10_axis_1_diagnosis:
      patientDiagnosis && patientDiagnosis.ICD_10_axis_1_diagnosis
        ? patientDiagnosis.ICD_10_axis_1_diagnosis
        : [],
    ICD_10_axis_2_diagnosis:
      patientDiagnosis && patientDiagnosis.ICD_10_axis_2_diagnosis
        ? patientDiagnosis.ICD_10_axis_2_diagnosis
        : [],
    note_state: "draft",
    provider_type: providerType,
    session_date: sessionDate,
    session_from: sessionFrom.format("HH:mm"),
    session_to: sessionTo.format("HH:mm"),
    title: title,
    version,
  };
};

const createSkinnyNote = (note, mainFields, contentFields) => {
  let transformedNote = {
    content: {},
  };

  copyFields(note, transformedNote, mainFields);

  if (note.content) {
    copyFields(note.content, transformedNote.content, contentFields);
  }

  return transformedNote;
};

export const enrichBehavioralNoteContent = (
  _patientDetails,
  questionSet,
  relevantSections,
  notesMap,
  _patientDiagnosis,
  // this prop should only be passed in when necessary, all
  // other goal and objectives should be set by the backend
  goalAndObjectiveFormV2,
) => {
  let initializeValues = true;
  let enrichedContent = {
    call_timer: "",
    exploration_notes: "",
    goals_objectives_progress: null,
    // these v2 fields are required so that the
    // goals and objectives are not overwritten
    // between saves
    goals_objectives_progress_v2: null,
    goals_objectives_progress_version: null,
    mental_status: _.cloneDeep(MENTAL_STATUS_QUESTIONS),
    patient_no_show: false,
    patient_no_show_reason: "",
    symptom_trend_notes: "",
    symptom_trends_substances: null,
  };

  if (notesMap.existingNote && notesMap.existingNote.content) {
    initializeValues = false;
    copyFields(notesMap.existingNote.content, enrichedContent, Object.keys(enrichedContent));
    // don't want to add to initial def of enrichedContent to avoid always having it be set
    if (Object.hasOwn(notesMap.existingNote.content, "discharges")) {
      enrichedContent["discharges"] = _.cloneDeep(notesMap.existingNote.content.discharges);
    }
  } else if (notesMap.lastNote && notesMap.lastNote.content) {
    copyFields(notesMap.lastNote.content, enrichedContent, ["mental_status"]);
  }

  if (initializeValues) {
    if (goalAndObjectiveFormV2) {
      enrichedContent.goals_objectives_progress_version = "2";
      enrichedContent.goals_objectives_progress_v2 = goalAndObjectiveFormV2;
    }
    enrichedContent.symptom_trends_substances = _.cloneDeep(SUBSTANCE_ABUSE_QUESTIONS);
  }

  enrichedContent.symptom_trends = transformPatientQuestionsToSection(
    questionSet,
    relevantSections.symptomTrends,
  );
  enrichedContent.changes = transformPatientQuestionsToSection(
    questionSet,
    relevantSections.changes,
    true,
    (answer) => {
      if (typeof answer === "boolean") {
        return answer;
      } else {
        return answer !== 2 && answer != null;
      }
    },
  );
  enrichedContent.health_care_use = transformPatientQuestionsToSection(
    questionSet,
    relevantSections.healthCareUse,
  );

  let lastPrescriberNote = _.get(notesMap, "lastNotesOther.PRESCRIBE");
  let skinnyPrescriberNote = null;

  if (lastPrescriberNote) {
    skinnyPrescriberNote = createSkinnyNote(
      lastPrescriberNote,
      ["id", "signature", "session_date", "version"],
      ["medications"],
    );
  }
  enrichedContent.last_prescribe_note = skinnyPrescriberNote;

  return enrichedContent;
};

const DEFAULT_PRESCRIPTION_MONITORING = {
  clicked: false,
  last_viewed: null,
  type: "I-STOP",
};

export const enrichPrescribeNoteContent = (
  patientDetails,
  questionSet,
  relevantSections,
  notesMap,
  patientDiagnosis,
) => {
  let initializeValues = true;
  let enrichedContent = {
    adherence_interventions: null,
    call_timer: "",
    diagnosis_progress: null,
    discharges: [],
    lab_findings: null,
    medical_information_notes: "",
    medication_impact_notes: "",
    medications: [],
    mental_status: _.cloneDeep(MENTAL_STATUS_QUESTIONS),
    patient_no_show: false,
    patient_no_show_reason: "",
    prescription_monitoring: _.cloneDeep(DEFAULT_PRESCRIPTION_MONITORING),
    side_effects: null,
    symptom_trend_notes: "",
  };

  if (notesMap.existingNote && notesMap.existingNote.content) {
    initializeValues = false;
    copyFields(notesMap.existingNote.content, enrichedContent, Object.keys(enrichedContent));
  } else if (notesMap.lastNote && notesMap.lastNote.content) {
    copyFields(notesMap.lastNote.content, enrichedContent, [
      "mental_status",
      "medications",
      "prescription_monitoring",
    ]);
    enrichedContent.medications = enrichedContent.medications.filter(
      (medication) => medication.med_changes !== "DISCONTINUE",
    );
    enrichedContent.medications = enrichedContent.medications.map((medication) => {
      if (medication.med_changes === "ADD") {
        medication.med_changes = "MAINTAIN";
      }

      return medication;
    });
  }
  if (initializeValues) {
    enrichedContent.diagnosis_progress = patientDiagnosis
      ? transformDiagnosisToProgress(patientDiagnosis)
      : [];
    enrichedContent.side_effects = _.cloneDeep(SIDE_EFFECT_QUESTIONS);
    enrichedContent.lab_findings = _.cloneDeep(LAB_FINDING_QUESTIONS);
    enrichedContent.adherence_interventions = _.cloneDeep(ADHERENCE_INTERVENTIONS_QUESTIONS);
  }

  enrichedContent.symptom_trends = transformPatientQuestionsToSection(
    questionSet,
    relevantSections.symptomTrends,
  );
  enrichedContent.medication_trends = transformPatientQuestionsToSection(
    questionSet,
    relevantSections.medicationTrends,
  );
  enrichedContent.medication_difficulties = transformPatientQuestionsToSection(
    questionSet,
    relevantSections.medicationDifficulties,
  );
  enrichedContent.medical_problems = transformPatientQuestionsToSection(
    questionSet,
    relevantSections.medicalProblems,
  );
  let lastBehavioralNote = _.get(notesMap, "lastNotesOther.BEHAVIORAL");
  let skinnyBehavioralNote = null;

  if (lastBehavioralNote) {
    skinnyBehavioralNote = createSkinnyNote(
      lastBehavioralNote,
      ["id", "signature", "session_date", "version"],
      ["symptom_trends", "symptom_trends_substances", "changes", "health_care_use"],
    );
  }
  enrichedContent.last_behavioral_note = skinnyBehavioralNote;

  return enrichedContent;
};

/**
 * @param {*} noteContent
 * @returns "1" or "2" this function will return "1" by default even if there are no goals/objectives
 * the components that use this need to take into account empty state
 */
export const getCareGoalAndObjectiveNoteVersion = (noteContent) => {
  return noteContent?.goals_objectives_progress_version || "1";
};

/**
 * gets the correct goals and objectives from the note based on the version
 * @param {*} noteContent
 * @param {*} version
 * @returns
 */
export const getCareGoalAndObjectivesFromNote = (noteContent, version) => {
  if (version === "1") {
    return noteContent?.goals_objectives_progress;
  } else if (version === "2") {
    return noteContent?.goals_objectives_progress_v2;
  }
};
